import React, { useCallback } from 'react';
import Button from '@/components/UI/Button';
import FormSelect from '@/components/UI/FormSelect';
import FormNumberInput from '@/components/UI/FormNumberInput';
import Modal from '@/components/UI/Modal';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { DirectionMaxRule } from '@einhaus/project314-types';

interface DirectionalRuleModalProps {
    isOpen: boolean;
    onClose: () => void;
    portfolioId: string | undefined;
    editingRuleId: string | null;
    newRule: Partial<DirectionMaxRule>;
    setNewRule: React.Dispatch<React.SetStateAction<Partial<DirectionMaxRule>>>;
    rules: DirectionMaxRule[];
    setRules: React.Dispatch<React.SetStateAction<DirectionMaxRule[]>>;
}

const DirectionalRuleModal: React.FC<DirectionalRuleModalProps> = ({
    isOpen,
    onClose,
    portfolioId,
    editingRuleId,
    newRule,
    setNewRule,
    rules,
    setRules
}) => {
    const queryClient = useQueryClient();

    // Mutation for saving directional rule (add/edit)
    const saveDirectionalRuleMutation = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });
            }
        }),
        {
            successMessage: 'Rule saved successfully',
            fallbackErrorMessage: 'Failed to save rule'
        }
    );

    const handleSaveDirectionalRule = useCallback(async () => {
        if (!portfolioId) return;

        const ruleId = editingRuleId ?? Date.now().toString();

        const rule = {
            type: newRule.type || 'positions',
            direction: newRule.direction || 'either',
            value: newRule.value || 0,
            id: ruleId
        };

        const previousRules = rules;

        const updatedRules =
            editingRuleId == null ? [...previousRules, rule] : previousRules.map((existing) => (existing.id === ruleId ? rule : existing));

        // Optimistically update UI
        setRules(updatedRules);
        onClose();

        try {
            await saveDirectionalRuleMutation.mutateAsync({
                portfolioId: parseInt(portfolioId),
                settingType: 'directionMax',
                value: rule
            });
        } catch {
            setRules(previousRules);
        }
    }, [portfolioId, editingRuleId, newRule, rules, saveDirectionalRuleMutation, onClose, setRules]);

    return (
        <Modal
            isOpen={isOpen}
            title={editingRuleId == null ? 'Add Underlying Exposure Limit Rule' : 'Edit Underlying Exposure Limit Rule'}
            onClose={onClose}
        >
            <div className="space-y-4">
                <p className="text-sm text-zinc-400">
                    Limits count underlying exposure. Either applies the position limit to bullish and bearish exposure separately.
                </p>
                <div className="mb-4">
                    <FormSelect
                        id="directionalRuleDirection"
                        label="Underlying exposure"
                        options={[
                            { value: 'long', label: 'Bullish' },
                            { value: 'short', label: 'Bearish' },
                            { value: 'either', label: 'Either exposure' }
                        ]}
                        value={newRule.direction}
                        onChange={(e) =>
                            setNewRule({
                                ...newRule,
                                direction: e.target.value as 'long' | 'short' | 'either'
                            })
                        }
                    />
                </div>
                <div className="mb-4">
                    <FormSelect
                        id="directionalRuleType"
                        label="Maximum Type"
                        options={[
                            { value: 'positions', label: 'Number of Positions' },
                            { value: 'percentage', label: 'Percentage of Portfolio' },
                            { value: 'value', label: 'Dollar Value' },
                            { value: 'positionCountGap', label: 'Position Count Gap' }
                        ]}
                        value={newRule.type}
                        onChange={(e) =>
                            setNewRule({
                                ...newRule,
                                type: e.target.value as 'positions' | 'percentage' | 'value' | 'positionCountGap'
                            })
                        }
                    />
                </div>
                <div className="mb-4">
                    <FormNumberInput
                        id="directionalRuleValue"
                        label="Maximum Value"
                        min={0}
                        value={newRule.value ?? null}
                        onValueChange={(value) =>
                            setNewRule({
                                ...newRule,
                                value: value ?? undefined
                            })
                        }
                    />
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                    <Button className="mr-2" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveDirectionalRule}>
                        {editingRuleId == null ? 'Add Rule' : 'Save Rule'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DirectionalRuleModal;
